import { useForm } from 'react-hook-form';

import { CreateReferralRequestInput, RespondentType } from './generated/referral-request';
import { type FormValues, getRespondentFormValue, isRespondentFieldRequired } from './respondent-fields';

type ReferralRequestFormValues = Pick<CreateReferralRequestInput, 'patientId' | 'respondents'>;

type DraftReferralRequestLike = {
  piSystemId?: string | null;
  respondents: {
    respondentType: RespondentType;
    givenName?: string | null;
    familyName?: string | null;
    phoneNumber?: string | null;
  }[];
};

const toFormValues = (draft: DraftReferralRequestLike, respondentTypes: RespondentType[]): FormValues => ({
  patientId: draft.piSystemId ?? '',
  respondents: Object.fromEntries(
    respondentTypes.map((respondentType) => {
      const existing = draft.respondents.find((respondent) => respondent.respondentType === respondentType);
      return [
        respondentType,
        {
          givenName: existing?.givenName ?? '',
          familyName: existing?.familyName ?? '',
          phoneNumber: existing?.phoneNumber ?? '',
        },
      ];
    })
  ) as FormValues['respondents'],
});

const toReferralRequestRespondents = (respondents: FormValues['respondents'], respondentTypes: RespondentType[]) =>
  respondentTypes
    .map((respondentType) => ({ respondentType, ...getRespondentFormValue(respondents[respondentType]) }))
    .filter(({ respondentType, ...respondent }) => isRespondentFieldRequired(respondent, respondentType));

const getRespondentErrorMessages = (
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors'],
  respondentType: RespondentType
) => ({
  familyNameErrorMessage: errors.respondents?.[respondentType]?.familyName?.message,
  givenNameErrorMessage: errors.respondents?.[respondentType]?.givenName?.message,
  phoneErrorMessage: errors.respondents?.[respondentType]?.phoneNumber?.message,
});

const isErrorMessage = (message: unknown): message is string => typeof message === 'string' && message.length > 0;

const getFormErrorMessages = (
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors'],
  respondentTypes: RespondentType[]
) =>
  respondentTypes
    .flatMap((respondentType) => Object.values(getRespondentErrorMessages(errors, respondentType)))
    .filter(isErrorMessage);

export type { DraftReferralRequestLike, ReferralRequestFormValues };
export { getFormErrorMessages, getRespondentErrorMessages, toFormValues, toReferralRequestRespondents };
